import { differenceInCalendarDays } from 'date-fns'

import type { SellerTutorialEvent } from '@/resources/types/seller'

export const PRACTICE_ORDER_EDIT_LOOKBACK_DAYS = 30
export const PRACTICE_ORDER_COOLDOWN_CALENDAR_DAYS = 2

export type ShouldShowPracticeOrderInput = {
  sellerOrderEdit?: SellerTutorialEvent
  sellerClosedTestOrder?: SellerTutorialEvent
  now: Date
}

const parseTimestamp = (value: string | null | undefined): Date | null => {
  if (!value) {
    return null
  }

  const parsedDate = new Date(value)

  if (Number.isNaN(parsedDate.getTime())) {
    return null
  }

  return parsedDate
}

const hasRecordedEvent = (
  event: SellerTutorialEvent | undefined
): event is SellerTutorialEvent => event != null && event.count >= 1

const calendarDaysSince = (
  event: SellerTutorialEvent | undefined,
  now: Date
): number | null => {
  if (!hasRecordedEvent(event)) {
    return null
  }

  const occurredAt = parseTimestamp(event.lastUpdate)

  if (!occurredAt) {
    return null
  }

  return differenceInCalendarDays(now, occurredAt)
}

export const shouldShowPracticeOrder = ({
  sellerOrderEdit,
  sellerClosedTestOrder,
  now,
}: ShouldShowPracticeOrderInput): boolean => {
  const daysSinceClosedTest = calendarDaysSince(sellerClosedTestOrder, now)
  const isCooldownActive =
    daysSinceClosedTest !== null &&
    daysSinceClosedTest < PRACTICE_ORDER_COOLDOWN_CALENDAR_DAYS

  if (isCooldownActive) {
    return false
  }

  const hasNeverEditedRealOrder = !hasRecordedEvent(sellerOrderEdit)

  if (hasNeverEditedRealOrder) {
    return true
  }

  const daysSinceRealEdit = calendarDaysSince(sellerOrderEdit, now)
  const hasRecentRealEdit =
    daysSinceRealEdit !== null &&
    daysSinceRealEdit >= 0 &&
    daysSinceRealEdit < PRACTICE_ORDER_EDIT_LOOKBACK_DAYS

  return !hasRecentRealEdit
}
